import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, ShieldAlert, Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children }) => {
  const { user, isAdmin, loading, login, logout } = useAuth();
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');
    try {
      await login();
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Gagal login. Silakan coba lagi.');
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF7A]"></div>
          <p className="mt-4 text-[#F7F5F2]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-md w-full">
          <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF7A]/20 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#D4AF7A]/10 rounded-full mb-6">
              <LogIn className="w-10 h-10 text-[#D4AF7A]" />
            </div>
            <h2 className="text-3xl font-bold text-[#F7F5F2] mb-3">
              Login Required
            </h2>
            <p className="text-[#F7F5F2]/70 mb-8">
              Anda harus login dengan Google untuk mengakses halaman admin ini.
            </p>

            {loginError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{loginError}</p>
              </div>
            )}

            <button
              onClick={handleGoogleLogin}
              disabled={loginLoading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white hover:bg-white/90 text-black font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {loginLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </>
              )}
            </button>

            <button
              onClick={() => window.history.back()}
              className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 text-[#F7F5F2] font-medium rounded-lg transition-all border border-[#D4AF7A]/30"
            >
              Kembali
            </button>
          </div>

          <p className="text-center mt-6 text-sm text-[#F7F5F2]/50">
            Hanya email tertentu yang diperbolehkan mengakses halaman admin.
          </p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        {/* Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-red-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-red-600/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-md w-full">
          <div className="bg-white/5 backdrop-blur-sm border border-red-500/30 rounded-2xl p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/10 rounded-full mb-6">
              <ShieldAlert className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-[#F7F5F2] mb-3">
              Akses Ditolak
            </h2>
            <p className="text-[#F7F5F2]/70 mb-4">
              Anda tidak memiliki izin untuk mengakses halaman admin ini.
            </p>
            <p className="text-[#F7F5F2]/50 mb-8 text-sm">
              Email yang terdaftar: <span className="text-[#D4AF7A]">{user?.email}</span>
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity"
              >
                Kembali ke Beranda
              </button>
              <button
                onClick={logout}
                className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 text-[#F7F5F2] font-medium rounded-lg transition-all border border-[#D4AF7A]/30"
              >
                Ganti Akun
              </button>
            </div>
          </div>

          <p className="text-center mt-6 text-sm text-[#F7F5F2]/50">
            Hubungi administrator jika Anda merasa ini adalah kesalahan.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
